import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { sprintf } from '@web-stories-wp/i18n';
import {
  discardInitialCspViolationBuffer,
  drainInitialCspViolationBuffer,
} from './cspViolationBuffer';

const APP_INSIGHTS_EVENT_ORIGINS = [
  'applicationinsights.azure.com',
  'dc.services.visualstudio.com',
];
const APP_INSIGHTS_EVENT_TIMEOUT = 3000;
const CSP_VIOLATION_EVENT_NAME = 'Studio: Content Security Policy Violation';
const MAX_CSP_VIOLATIONS_PER_PAGE = 50;
const CSP_BLOCKED_TARGET_KEYWORDS = new Map([
  ['blob', 'blob:'],
  ['blob:', 'blob:'],
  ['data', 'data:'],
  ['data:', 'data:'],
  ['eval', 'eval'],
  ['filesystem', 'filesystem:'],
  ['filesystem:', 'filesystem:'],
  ['inline', 'inline'],
  ['wasm-eval', 'wasm-eval'],
]);
const CSP_DIRECTIVE_PATTERN = /^[a-z-]{1,64}$/;
const URL_PATTERN = /https?:\/\/[^\s"'<>)]+/gi;
const SENSITIVE_UPLOAD_HOSTS = ['assetuploads.usestoryteller.com'];
const SENSITIVE_QUERY_PARAMETERS = [
  'access_token',
  'token',
  'x-amz-credential',
  'x-amz-security-token',
  'x-amz-signature',
];
const TELEMETRY_URL_FIELDS = ['uri', 'url', 'refUri', 'data', 'target', 'name'];

export const EntityType = {
  Page: 'Page',
  Story: 'Story',
  Clip: 'Clip',
  ContentBankAsset: 'ContentBankAsset',
  CardsAsset: 'CardsAsset',
  Poll: 'Poll',
  TriviaQuiz: 'TriviaQuiz',
  InterstitialAd: 'InterstitialAd',
  Template: 'Template',
};

export const EventTargetElement = {
  /** Story */
  StoryPage: 'StoryPage',
  StoryIcon: 'StoryIcon',
  StoryThumbnail: 'StoryThumbnail',

  /** Clip */
  ClipAsset: 'ClipAsset',
  ClipThumbnail: 'ClipThumbnail',

  /** Content Bank */
  ContentBankAsset: 'ContentBankAsset',

  /** Cards */
  CardsAsset: 'CardsAsset',

  /** Poll */
  PollQuestion: 'PollQuestion',
  PollAnswer: 'PollAnswer',

  /** Interstitial Ad */
  InterstitialAd: 'InterstitialAd',

  /** Template */
  Template: 'Template',
};

export function initAppInsights() {
  try {
    initAppInsightsInternal();
  } catch (e) {
    discardInitialCspViolationBuffer();
    console.error('AppInsights initialization failed.');
  }
}

function initAppInsightsInternal() {
  if (!process.env.APP_INSIGHTS_CONNECTION_STRING) {
    discardInitialCspViolationBuffer();
    return;
  }

  const appInsights = new ApplicationInsights({
    config: {
      connectionString: process.env.APP_INSIGHTS_CONNECTION_STRING,
    },
  });

  window.appInsights = appInsights;

  appInsights.loadAppInsights();
  appInsights.addTelemetryInitializer(sanitizeTelemetryItemUrls);
  appInsights.addTelemetryInitializer((item) => {
    item.tags = item.tags || {};
    item.tags['ai.application.ver'] = process.env.REACT_APP_STUDIO_VERSION;
    return true;
  });
  configureCspViolationTelemetry(appInsights);
  appInsights.trackPageView({
    uri: sanitizeAppInsightsUrl(window.location.href),
  });

  configureInsightsForLogs();

  console.log('AppInsights initialized.');
}

function sanitizeCspTelemetryToken(value) {
  return typeof value === 'string' && CSP_DIRECTIVE_PATTERN.test(value)
    ? value
    : 'unknown';
}

function sanitizeCspDirective(event) {
  const directive = event.effectiveDirective || event.violatedDirective;
  const directiveName =
    typeof directive === 'string'
      ? directive.trim().split(/\s+/)[0].toLowerCase()
      : directive;

  return sanitizeCspTelemetryToken(directiveName);
}

export function sanitizeCspBlockedTarget(value) {
  if (typeof value !== 'string' || !value.trim()) {
    return 'unknown';
  }

  const normalizedValue = value.trim().toLowerCase();
  if (CSP_BLOCKED_TARGET_KEYWORDS.has(normalizedValue)) {
    return CSP_BLOCKED_TARGET_KEYWORDS.get(normalizedValue);
  }

  try {
    const url = new URL(value);
    if (['blob:', 'data:', 'filesystem:'].includes(url.protocol)) {
      return url.protocol;
    }

    return url.origin === 'null' ? 'opaque' : url.origin;
  } catch (e) {
    return 'opaque';
  }
}

export function configureCspViolationTelemetry(
  appInsights,
  eventTarget = document,
  violationBuffer
) {
  const reportedViolations = new Set();
  const handleViolation = (event) => {
    const properties = {
      BlockedTarget: sanitizeCspBlockedTarget(event.blockedURI),
      Disposition:
        event.disposition === 'enforce' || event.disposition === 'report'
          ? event.disposition
          : 'unknown',
      EffectiveDirective: sanitizeCspDirective(event),
    };
    const fingerprint = JSON.stringify(properties);

    if (
      reportedViolations.has(fingerprint) ||
      reportedViolations.size >= MAX_CSP_VIOLATIONS_PER_PAGE
    ) {
      return;
    }

    reportedViolations.add(fingerprint);
    appInsights.trackEvent({ name: CSP_VIOLATION_EVENT_NAME, properties });
  };

  eventTarget.addEventListener('securitypolicyviolation', handleViolation);
  if (violationBuffer) {
    violationBuffer.drain(handleViolation);
  } else if (typeof document !== 'undefined' && eventTarget === document) {
    drainInitialCspViolationBuffer(handleViolation);
  }
  return () =>
    eventTarget.removeEventListener('securitypolicyviolation', handleViolation);
}

export function sanitizeAppInsightsUrl(value) {
  if (typeof value !== 'string') {
    return value;
  }

  const sanitized = sanitizeStringUrls(value);
  const hashIndex = sanitized.indexOf('#');

  if (hashIndex === -1) {
    return sanitized;
  }

  return sanitized.substring(0, hashIndex);
}

export function sanitizeStringUrls(value) {
  return value.replace(URL_PATTERN, sanitizeUrl);
}

function sanitizeUrl(urlValue) {
  try {
    const url = new URL(urlValue);
    url.hash = '';

    if (shouldStripQueryString(url)) {
      url.search = '';
    }

    return url.toString();
  } catch (e) {
    const hashIndex = urlValue.indexOf('#');
    return hashIndex === -1 ? urlValue : urlValue.substring(0, hashIndex);
  }
}

function shouldStripQueryString(url) {
  const queryKeys = Array.from(url.searchParams.keys()).map((key) =>
    key.toLowerCase()
  );

  return (
    SENSITIVE_UPLOAD_HOSTS.includes(url.hostname) ||
    SENSITIVE_QUERY_PARAMETERS.some((parameter) =>
      queryKeys.includes(parameter)
    )
  );
}

export function sanitizeTelemetryItemUrls(item) {
  sanitizeTelemetryFields(item?.baseData);
  sanitizeTelemetryFields(item?.data);

  return true;
}

function sanitizeTelemetryFields(fields) {
  if (!fields) {
    return;
  }

  TELEMETRY_URL_FIELDS.forEach((field) => {
    if (typeof fields[field] === 'string') {
      fields[field] = sanitizeAppInsightsUrl(fields[field]);
    }
  });

  sanitizeTelemetryProperties(fields.properties);
  fields.exceptions = sanitizeTelemetryProperties(fields.exceptions);
}

function sanitizeTelemetryProperties(value, visited = new WeakSet()) {
  if (typeof value === 'string') {
    return sanitizeStringUrls(value);
  }

  if (!value || typeof value !== 'object') {
    return value;
  }

  if (visited.has(value)) {
    return value;
  }

  visited.add(value);

  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      value[index] = sanitizeTelemetryProperties(item, visited);
    });
    return value;
  }

  Object.keys(value).forEach((key) => {
    value[key] = sanitizeTelemetryProperties(value[key], visited);
  });

  return value;
}

function configureInsightsForLogs() {
  var nativeConsole = window.console;

  window.console = {
    ...nativeConsole,
    error: function (error, ...optionalParams) {
      if (!error) {
        return;
      }

      const ERROR_NAME = 'Studio: Console Error';

      try {
        if (error instanceof Error) {
          trackAppInsightsError(ERROR_NAME, error);
        } else if (typeof error === 'string' || error instanceof String) {
          const formattedMessage = sprintf(error, ...optionalParams);

          if (!error.startsWith('Warning: ')) {
            throw new Error(formattedMessage);
          }

          trackAppInsightsError(ERROR_NAME, new Error(formattedMessage));
          // Errors created by trackError have a description but no message
        } else if (
          'description' in error &&
          typeof error.description === 'string'
        ) {
          trackAppInsightsError(ERROR_NAME, error.description);
        } else {
          trackAppInsightsError(
            'Studio: Unsupported Error Format',
            new Error('Unsupported Error Format'),
            JSON.stringify({ error, optionalParams })
          );
        }
      } catch (formattedError) {
        nativeConsole.error(formattedError.message);
      }
    },
  };
}

export function trackAppInsightsError(name, exception, payload = {}) {
  const properties = {
    ...getAppInsightsProperties(),
    ...(payload ? { Payload: payload } : {}),
  };

  if (!window.appInsights) {
    throw new Error(
      `AppInsights is not initialized. Error: ${name}: ${exception.message}`
    );
  }

  window.appInsights.trackException({
    exception,
    properties: {
      ...properties,
    },
  });

  window.appInsights.trackEvent({
    name,
    properties: {
      ...properties,
      Error: exception.message,
    },
  });
}

export async function trackAppInsightsEvent(name, payload = {}, flush = false) {
  const properties = {
    ...getAppInsightsProperties(),
    ...(payload ? { Payload: payload } : {}),
  };

  if (!window.appInsights) {
    console.warn(
      `AppInsights is not initialized. The following event was not tracked: ${name}.`
    );
    return;
  }

  window.appInsights.trackEvent({
    name,
    properties,
  });

  // If flush is required, wait until the request is completed
  if (flush) {
    // Wait for App Insights flush to complete
    await window.appInsights.flush({ isAsync: true });

    // Create a promise that resolves when the request is completed
    return new Promise((resolve) => {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (
            APP_INSIGHTS_EVENT_ORIGINS.some((origin) =>
              entry.name.includes(origin)
            ) &&
            entry.responseStatus !== 0
          ) {
            observer.disconnect();
            resolve();
            return;
          }
        }
      });

      // Start observing network requests
      observer.observe({
        entryTypes: ['resource'],
      });

      // Set a timeout in case the request never completes
      setTimeout(() => {
        observer.disconnect();
        resolve();
      }, APP_INSIGHTS_EVENT_TIMEOUT);
    });
  }
}

function getAppInsightsProperties() {
  const queryString = window?.location?.search;
  const urlParams = new URLSearchParams(queryString);

  const tenantName = window?.location?.host?.split('.')?.[0];
  const storyId = urlParams.get('storyId');
  const assetType = urlParams.get('assetType');
  const resourceId = urlParams.get('resourceId');

  const properties = {
    ...(assetType ? { AssetType: assetType } : {}),
    ...(resourceId ? { ResourceId: resourceId } : {}),
    ...(storyId ? { StoryId: storyId } : {}),
    Tenant: tenantName,
  };

  return properties;
}
